import {
  useRef,
  useState,
  useCallback,
  useEffect,
  Fragment,
  type ReactNode,
} from "react";
import classNames from "classnames";
import Icon from "@mdi/react";
import { mdiPencilOutline } from "@mdi/js";
import { Button } from "@heroui/react";

export interface SortableSettingsListProps<T extends { uuid: string }> {
  items: T[];
  orderEditMode: boolean;
  onReorder?: (fromIndex: number, toIndex: number) => void;
  isSaving?: boolean;
  onEdit?: (item: T) => void;
  renderContent: (item: T) => ReactNode;
}

function getIndexFromElement(element: Element | null): number | null {
  const row = element?.closest("[data-sortable-index]");
  if (!row) return null;
  const value = row.getAttribute("data-sortable-index");
  if (value === null || value === "") return null;
  const index = Number(value);
  return Number.isNaN(index) ? null : index;
}

function DragHandle({
  disabled,
  onPointerDown,
}: {
  disabled?: boolean;
  onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      className={classNames("fbs-list__drag-handle", {
        "fbs-list__drag-handle--disabled": disabled,
      })}
      aria-label="Переместить"
      onPointerDown={onPointerDown}
      onKeyDown={(e) => e.preventDefault()}
    >
      <span className="fbs-list__drag-grip" aria-hidden="true" />
    </div>
  );
}

function SortableSettingsList<T extends { uuid: string }>({
  items,
  orderEditMode,
  onReorder,
  isSaving = false,
  onEdit,
  renderContent,
}: SortableSettingsListProps<T>) {
  const dragFromRef = useRef<number | null>(null);
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
  const ghostOffsetRef = useRef({ x: 0, y: 0 });
  const dragOverRef = useRef<number | null>(null);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const [draggedHeight, setDraggedHeight] = useState(0);
  const [ghostPosition, setGhostPosition] = useState<{
    x: number;
    y: number;
    width: number;
  } | null>(null);

  const resetDrag = useCallback(() => {
    dragFromRef.current = null;
    dragOverRef.current = null;
    setDragFrom(null);
    setDragOver(null);
    setDraggedHeight(0);
    setGhostPosition(null);
  }, []);

  const updateDragOver = useCallback((clientX: number, clientY: number) => {
    const over = getIndexFromElement(
      document.elementFromPoint(clientX, clientY),
    );
    if (over !== null && dragOverRef.current !== over) {
      dragOverRef.current = over;
      setDragOver(over);
    }
  }, []);

  const finishDrag = useCallback(
    (event: PointerEvent, commit: boolean) => {
      if (dragFromRef.current === null) return;

      const from = dragFromRef.current;
      const over =
        getIndexFromElement(document.elementFromPoint(event.clientX, event.clientY)) ??
        dragOverRef.current;

      if (commit && over !== null && over !== from) {
        onReorder?.(from, over);
      }

      resetDrag();
    },
    [onReorder, resetDrag],
  );

  useEffect(() => {
    if (dragFrom === null) return;

    const handlePointerMove = (event: PointerEvent) => {
      event.preventDefault();
      setGhostPosition((prev) =>
        prev
          ? {
              ...prev,
              x: event.clientX - ghostOffsetRef.current.x,
              y: event.clientY - ghostOffsetRef.current.y,
            }
          : null,
      );
      updateDragOver(event.clientX, event.clientY);
    };

    const handlePointerUp = (event: PointerEvent) => {
      finishDrag(event, true);
    };

    const handlePointerCancel = (event: PointerEvent) => {
      finishDrag(event, false);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerCancel);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerCancel);
    };
  }, [dragFrom, finishDrag, updateDragOver]);

  const handlePointerDown = (
    event: React.PointerEvent<HTMLDivElement>,
    index: number,
  ) => {
    if (!orderEditMode || isSaving || event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();

    const node = itemRefs.current[index];
    const rect = node?.getBoundingClientRect();
    if (rect) {
      setDraggedHeight(rect.height);
      ghostOffsetRef.current = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      setGhostPosition({
        x: rect.left,
        y: rect.top,
        width: rect.width,
      });
    }

    dragFromRef.current = index;
    dragOverRef.current = index;
    setDragFrom(index);
    setDragOver(index);
  };

  const isDragging = dragFrom !== null;
  const hasDropPreview =
    isDragging && dragOver !== null && dragFrom !== dragOver;

  const renderPlaceholder = (key: string) => (
    <li
      key={key}
      className="fbs-list__placeholder"
      style={{ height: draggedHeight || undefined }}
      aria-hidden="true"
    />
  );

  const setItemRef = (index: number) => (node: HTMLLIElement | null) => {
    itemRefs.current[index] = node;
  };

  return (
    <>
      {ghostPosition !== null && dragFrom !== null && (
        <div
          className="fbs-list__ghost"
          style={{
            transform: `translate(${ghostPosition.x}px, ${ghostPosition.y}px)`,
            width: ghostPosition.width,
          }}
          aria-hidden="true"
        >
          <div className="fbs-list__ghost-inner">
            {renderContent(items[dragFrom])}
          </div>
        </div>
      )}
      <ul
        className={classNames("fbs-list", {
          "fbs-list--reordering": isSaving,
          "fbs-list--dragging": isDragging,
          "fbs-list--order-edit": orderEditMode,
        })}
      >
        {items.map((item, index) => {
          const isDragSource = dragFrom === index;
          const isDragSourceCollapsed = isDragSource && hasDropPreview;
          const isDragSourceSpacer = isDragSource && !hasDropPreview;
          const showPlaceholderBefore =
            hasDropPreview && dragFrom > dragOver && index === dragOver;
          const showPlaceholderAfter =
            hasDropPreview && dragFrom < dragOver && index === dragOver;

          if (isDragSourceSpacer) {
            return (
              <li
                key={item.uuid}
                ref={setItemRef(index)}
                data-sortable-index={index}
                className="fbs-list__placeholder fbs-list__placeholder--source"
                style={{ height: draggedHeight || undefined }}
                aria-hidden="true"
              />
            );
          }

          if (isDragSourceCollapsed) {
            return (
              <li
                key={item.uuid}
                ref={setItemRef(index)}
                data-sortable-index={index}
                className="fbs-list__item fbs-list__item--dragging-collapsed"
                aria-hidden="true"
              />
            );
          }

          return (
            <Fragment key={item.uuid}>
              {showPlaceholderBefore &&
                renderPlaceholder(`placeholder-before-${index}`)}
              <li
                ref={setItemRef(index)}
                data-sortable-index={index}
                className={classNames("fbs-list__item", {
                  "fbs-list__item--no-handle": !orderEditMode,
                })}
              >
                {orderEditMode && (
                  <DragHandle
                    disabled={isSaving}
                    onPointerDown={(e) => handlePointerDown(e, index)}
                  />
                )}
                {orderEditMode ? (
                  <div className="fbs-list__main">{renderContent(item)}</div>
                ) : (
                  <button
                    type="button"
                    className="fbs-list__main fbs-list__main--clickable"
                    onClick={() => onEdit?.(item)}
                  >
                    {renderContent(item)}
                  </button>
                )}
                {!orderEditMode && (
                  <div className="fbs-list__actions">
                    <Button
                      isIconOnly
                      variant="ghost"
                      size="sm"
                      aria-label="Редактировать"
                      onPress={() => onEdit?.(item)}
                    >
                      <Icon path={mdiPencilOutline} size={0.85} />
                    </Button>
                  </div>
                )}
              </li>
              {showPlaceholderAfter &&
                renderPlaceholder(`placeholder-after-${index}`)}
            </Fragment>
          );
        })}
      </ul>
    </>
  );
}

SortableSettingsList.displayName = "SortableSettingsList";
export default SortableSettingsList;
